import { PropType } from 'vue-demi';
import { RouteConfig } from 'vue-router';

import { HITL, HttpSDK } from '@/api';
import { IAgent } from '@/types';
import { AppLauncherSettings, MigrationsBySemVerRange, VersionInfo, AppError } from './types';

/** SDKs are external dependencies that are mocked in stories */
export const SDK = { HITL, HttpSDK };

/** Min HITL version that can be used with App Launcher. */
export const APP_MIN_VERSION = '0.62.0';
export const MIN_VERSION = '0.0.0';

/** App states are basically routes that aren't visible to the user */
export enum APP_STATES {
  /* "Default" is a dummy state that has no content */
  DEFAULT = 'default',
  /* Multiple warning states with or without additional actions */
  NO_AGENT_ACCESS = 'warning-no-agent-access',
  NO_APP_AVAILABLE = 'warning-no-app-available',
  NO_APP_VERSION = 'warning-no-app-version',
  NO_APP_VERSION_SUPPORT = 'warning-no-app-version-support',
  NEEDS_DATA_UPDATE = 'warning-needs-data-update',
  DATA_UPDATE_ERROR_ADMIN = 'warning-data-update-error-admin',
  DATA_UPDATE_ERROR_AGENT = 'warning-data-update-error-agent',
  /* This state should be combined with "component" store property */
  APP_BUNDLE = 'app-bundle',
  APP_SETTINGS = 'app-settings',
  APP_IS_UPDATING = 'app-is-updating',
  /* This state indicates unrecoverable error and overrides all other states */
  APP_ERROR = 'app-error',
}

export enum APP_KEYS {
  AGENT_UI = 'agent-ui',
  UI_CUSTOMIZER = 'ui-customizer',
  APP_SETTINGS = 'app-settings',
  APP_LAUNCHER = 'app-launcher',
}

/** App launcher is not included in "Go To App" feature */
export type AppKey = APP_KEYS.AGENT_UI | APP_KEYS.APP_SETTINGS | APP_KEYS.UI_CUSTOMIZER;
export type AppState = APP_STATES;

export const APPS = {
  [APP_KEYS.AGENT_UI]: {
    key: APP_KEYS.AGENT_UI,
    label: 'HITL.common.apps.agent-ui',
    icon: 'dashboard',
    path: 'agent-ui.umd.min.js',
  },
  [APP_KEYS.APP_SETTINGS]: {
    key: APP_KEYS.APP_SETTINGS,
    label: 'HITL.common.apps.app-settings',
    icon: 'settings',
    path: 'app-settings.umd.min.js',
  },
  [APP_KEYS.UI_CUSTOMIZER]: {
    key: APP_KEYS.UI_CUSTOMIZER,
    label: 'HITL.common.apps.agent-ui-customizer',
    icon: 'brush',
    path: 'ui-customizer.umd.min.js',
  },
};

/**
 * Routes are used only to determine starting point - all navigation is done using state.
 * Some routes are duplicated so we can use name to change URL.
 * URLs that don't match any route are considered to belong to Agent UI.
 */
export const APP_LAUNCHER_ROUTES = [
  { path: '/', name: APP_KEYS.AGENT_UI },
  { path: '/settings', name: APP_KEYS.APP_SETTINGS },
  { path: '/settings/*', name: APP_KEYS.APP_SETTINGS },
  { path: '/customization', name: APP_KEYS.UI_CUSTOMIZER },
  { path: '/customization/*', name: APP_KEYS.UI_CUSTOMIZER },
  { path: '/launcher', name: APP_KEYS.APP_LAUNCHER },
  { path: '/launcher/*', name: APP_KEYS.APP_LAUNCHER },
  { path: '/*', name: APP_KEYS.AGENT_UI },
] as RouteConfig[];

/** Milestone migrations required in some launcher features */
export const MIGRATIONS = {
  SETTINGS: '01-copy-settings-sql',
  AGENTS: '23-copy-agents-sql',
  CONTACT_RULE_GROUPS: '28-add-contact-rule-groups-to-agents',
  NEW_CONTACTS: '29-copy-contacts-sql',
};

export const DEFAULT_APP_SETTINGS = {
  appVersion: '',
  showRCVersions: false,
  skipAppLauncher: false,
};

export const DEFAULT_AVATAR_SETTINGS = {
  rounded: true,
  custom: { isEnabled: false },
};

export const AVATAR_PROP = {
  type: Object,
  default: undefined,
};

export const AGENT_PROP = {
  type: Object as PropType<IAgent>,
  default: undefined,
};

export const APP_STATE_PROP = {
  type: String as PropType<AppState>,
  default: APP_STATES.DEFAULT,
};

export const APP_KEY_PROP = {
  type: String as PropType<AppKey>,
  default: APP_KEYS.AGENT_UI,
};

export const APP_SETTINGS_PROP = {
  type: Object as PropType<AppLauncherSettings>,
  default: () => ({}),
};

export const APP_VERSIONS_PROP = {
  type: Array as PropType<VersionInfo[]>,
  default: () => [],
};

export const MIGRATIONS_BY_RANGE_PROP = {
  type: Object as PropType<MigrationsBySemVerRange>,
  default: () => ({}),
};

export const ACTIONS_TYPE_PROP = {
  type: String as PropType<'' | 'save' | 'update'>,
  default: '',
};

export const APP_VERSION_PROP = {
  type: String,
  default: '',
};

export const APP_ERROR_PROP = {
  type: [Object, Error] as PropType<AppError>,
  default: undefined,
};
